import styled, { css } from 'styled-components';

/**
 * The cards the AI triage bot hangs under a message: an action waiting to be
 * confirmed, and a support ticket. Yes / No buttons on a question are not a
 * card - they sit inside the question's own bubble - but they borrow the
 * button row and the status line from here, so a question and a card ask in
 * the same voice.
 *
 * They are their own white surface stacked in `BubbleShell`, like the file
 * chips and unlike the reply strip, for two reasons. A card is a form rather
 * than part of the sentence above it - the merchant reads the message, then
 * acts. And on the admin side the bot's message is on the vGrubs side and
 * therefore red, where a card drawn inside the bubble would have to restyle
 * every control it holds.
 */

export const CardSurface = styled.div`
  /*
   * Positioned on purpose, though nothing here is offset: the bubble above a
   * card in the same message IS positioned (it anchors its own meta), and
   * among elements with z-index auto the positioned one paints last - so its
   * shadow landed on top of the card and drew a dark band across its head.
   * Positioning this too puts them in one order, and the card is later.
   */
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 14px;
  background: ${({ theme }) => theme.white};
  border: ${({ theme }) => theme.borders.border1};
  box-shadow: ${({ theme }) => theme.shadows.shadow1};
  color: ${({ theme }) => theme.darkGray};
  font-size: 13px;
  line-height: 18px;
  /* One width for every card, rather than each shrinking to its own longest
     line: a run of them is a column down one side of the thread, and sized by
     their content a status chip and a list of orders make a ragged edge that
     reads as three different kinds of thing. max-width, not min(), because the
     shell around it is shrink-to-fit - 100% there resolves to the content
     width, which is the very thing being overridden. */
  width: 340px;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 800px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

/** The kind of request, over the bot's own sentence. */
export const CardKicker = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.lightGray};

  svg {
    flex: none;
    display: block;
  }
`;

/** What the merchant is agreeing to, in the backend's words. */
export const CardTitle = styled.div`
  color: ${({ theme }) => theme.darkGray};
  white-space: pre-wrap;
  word-break: break-word;
`;

export type CardTone = 'wait' | 'info' | 'good' | 'bad' | 'neutral';

/*
 * White on a solid fill, the way every status in this app is drawn - the same
 * four colours the delivery timeline gives to waiting, working, done and
 * failed, and its grey for the ones that simply stopped mattering.
 */
const toneColors = {
  wait: css`
    background: ${({ theme }) => theme.yellow};
  `,
  info: css`
    background: ${({ theme }) => theme.darkBlue};
  `,
  good: css`
    background: ${({ theme }) => theme.green};
  `,
  bad: css`
    background: ${({ theme }) => theme.primary};
  `,
  neutral: css`
    background: ${({ theme }) => theme.lightGray};
  `,
};

/**
 * Where the request stands, once there is nothing left to press.
 *
 * Inline-flex rather than a block: several of these read as a sentence with a
 * highlight, and a full-width band would make every finished card look like a
 * warning.
 */
export const CardChip = styled.span<{ $tone: CardTone }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.white};
  ${({ $tone }) => toneColors[$tone]}
`;

export const CardButtons = styled.div<{ $wrap?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  /* A little more air above than the card's own 8px gap: the buttons are the
     one thing here that is pressed, not read. */
  margin-top: 4px;

  /* The buttons carry $small themselves; this only makes them share the row
     rather than keep that variant's fixed width. */
  button {
    flex: 1;
    min-width: 0;
  }

  /*
   * The shared spinner hangs 78px past the right edge of its button, which is
   * room a full width form button has and a pill in a 340px card does not: it
   * spins on top of the label instead, worst of all in the floating bubble.
   *
   * So it moves to the middle and the label steps aside for it. Hidden rather
   * than unmounted, so the button keeps the width it had and the row does not
   * jump while the request is in flight.
   */
  button[data-loading='true'] {
    span {
      visibility: hidden;
    }

    > div {
      right: auto;
      left: 50%;
    }
  }

  /*
   * Three buttons instead of two, and one of them carries a phrase.
   *
   * They wrap rather than stack: the two ways of saying yes share the first
   * row and Cancel drops under them, which reads as one choice instead of a
   * column of three identical slabs. Last in the file on purpose - it has to
   * win over the flex line above.
   *
   * The width is the load-bearing part. A $small button is 140px wide and a
   * flex item cannot be stretched past an explicit width, so without this the
   * buttons stay narrow and a label like Pause indefinitely breaks across two
   * lines inside a button 32px tall.
   */
  ${({ $wrap }) =>
    $wrap &&
    `
    flex-wrap: wrap;

    button {
      flex: 1 1 auto;
      width: auto;
      white-space: nowrap;
    }
  `}
`;

/**
 * Yes / No inside the bot's question bubble.
 *
 * The card's button row, capped at the width it has in a card (340px less the
 * padding). A bubble grows with its sentence, and across a long one the two
 * buttons stretched into slabs half a screen wide - the same pair should look
 * the same in both places.
 *
 * The time the bubble floats after its text lands under this row, the way a
 * live card's time sits under its buttons.
 */
export const BubbleButtons = styled(CardButtons)`
  max-width: 316px;
  margin-top: 10px;
`;

/**
 * A list to pick one from: the locations under "Which location is this for?",
 * the orders on a ticket. Drawn the way the merchant app lists places to pick
 * from - grey rows, the main line over a lighter one, a chevron at the end -
 * so the bot asks the same kind of question the same way wherever it asks it.
 *
 * A column rather than the Yes / No row: each row is a name and a street, or a
 * whole order, and two of them side by side would break mid-word.
 */
export const ChoiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/** The same list inside the bot's bubble, set off from the question above. */
export const BubbleChoices = styled(ChoiceList)`
  margin-top: 10px;
`;

/** "Which order is it?" above the candidates. */
export const CardPrompt = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.lightGray};
`;

/** The grey box a choice sits in, whether or not it can be pressed. */
const choiceSurface = css`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 9px 12px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.gray};
  background: ${({ theme }) => theme.gray};
  /* A button does not inherit the page's font. */
  font-family: inherit;
  text-align: left;
  white-space: normal;

  /* Roomier on a phone, where each row is a finger's target. */
  @media (max-width: 800px) {
    padding: 11px 14px;
  }
`;

/**
 * One row the merchant can tap: a location, or an order on a ticket.
 *
 * Once tapped, every row locks while the answer is sent, and the tapped one
 * keeps its full colour with a red edge - dimmed with the rest, nothing would
 * say which one the merchant just picked. The edge is there all along in the
 * row's own grey, so lighting it up does not move the text.
 */
export const ChoiceRow = styled.button<{ $picked?: boolean }>`
  ${choiceSurface}
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.ease200ms};

  svg {
    flex: none;
  }

  :hover:enabled {
    border-color: ${({ theme }) => theme.mediumGray};
    background: ${({ theme }) => theme.mediumGray};
  }

  :disabled {
    opacity: 0.5;
    cursor: default;
  }

  ${({ $picked, theme }) =>
    $picked &&
    css`
      :disabled {
        opacity: 1;
        border-color: ${theme.primary};
      }
    `}
`;

/**
 * The single order on a ticket, which is a statement rather than a choice -
 * the Confirm under it answers it. No chevron and no hover: a row that looks
 * tappable and does nothing is worse than a plain box.
 */
export const ChoiceLine = styled.div`
  ${choiceSurface}
`;

/** The two lines of a row, taking whatever the chevron leaves. */
export const ChoiceText = styled.span`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  word-break: break-word;
`;

/* On a phone the bubble's own text is 16px, and the row's main line matches it
   rather than reading as fine print on the one thing there is to do here. */
export const ChoiceTitle = styled.span`
  color: ${({ theme }) => theme.darkGray};
  font-size: 14px;
  line-height: 18px;
  font-weight: 700;

  @media (max-width: 800px) {
    font-size: 16px;
    line-height: 21px;
  }
`;

export const ChoiceDetail = styled.span`
  color: ${({ theme }) => theme.lightGray};
  font-size: 13px;
  line-height: 18px;

  @media (max-width: 800px) {
    font-size: 15px;
    line-height: 20px;
  }
`;

/** "Open ticket", on the admin's read-only card. */
export const CardLink = styled.span`
  align-self: flex-start;
  color: ${({ theme }) => theme.primary};
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  font-size: 12px;
  line-height: 16px;
`;

/**
 * Holds the timestamp when the card is the whole message and nothing above it
 * could carry one - the same job MediaMeta does for a lone file chip.
 */
export const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`;

/**
 * The status and the time on one line, facing each other.
 *
 * A finished card is a chip and a timestamp and nothing else, and stacked they
 * left a band of empty card under the chip - the eye reads that as something
 * missing rather than as breathing room.
 */
export const CardStatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;
